

export function parseRecord(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== "string") {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function nonEmptyRecord(raw: unknown): Record<string, unknown> | null {
  const record = parseRecord(raw);
  return Object.keys(record).length ? record : null;
}

export function required<T extends Element>(root: ShadowRoot, selector: string): T {
  const element = root.querySelector(selector);
  if (!element) {
    throw new Error(`Agent Platform widget missing element: ${selector}`);
  }
  return element as T;
}

export function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    const replacements: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;"
    };
    return replacements[char] ?? char;
  });
}

export function publicVisitorText(value: string): string {
  const bracketedReference = new RegExp(
    `\\s*[\\[(](?:(?:source|citation)\\s*:?\\s*)?${publicInternalUuidPattern}` +
      `(?:\\s*[,;]\\s*(?:(?:source|citation)\\s*:?\\s*)?${publicInternalUuidPattern})*[\\])]`,
    "gi"
  );
  const internalUuid = new RegExp(`\\b${publicInternalUuidPattern}\\b`, "i");
  const internalField =
    /\b(?:chunk|document|invocation|trace|runtime[_ -]?manifest|lancedb(?:[_ -]?(?:row|table))?)[_ -]?id\b/i;
  const cleaned = value
    .replace(bracketedReference, "")
    .replace(/\s+([,.;:!?])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  if (internalUuid.test(cleaned) || internalField.test(cleaned)) {
    return "I couldn’t safely format that response. Please try again.";
  }
  return cleaned;
}

export function publicVisitorSpeech(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const speech = { ...(value as Record<string, unknown>) };
  for (const key of ["content", "text"] as const) {
    if (typeof speech[key] === "string") {
      speech[key] = publicVisitorText(speech[key]);
    }
  }
  return speech;
}

export function boundedActionAttribute(value: string, maximumLength: number): string {
  return value.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, maximumLength);
}

export function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function booleanValue(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    return !["false", "0", "no", "off", "disabled"].includes(value.trim().toLowerCase());
  }
  return fallback;
}

export function positiveInteger(value: unknown): number | null {
  const numeric =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() ? Number.parseInt(value.trim(), 10) : Number.NaN;
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric) : null;
}

export function numberAttribute(element: HTMLElement, name: string): number | null {
  const raw = element.getAttribute(name);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseJsonAttribute(value: string): Record<string, unknown> | null {
  if (!value) {
    return null;
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function recordArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === "object" && !Array.isArray(item)))
    : [];
}

export function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0) : [];
}

export function safeUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    if (parsed.username || parsed.password) {
      return false;
    }
    if (parsed.protocol === "https:") {
      return true;
    }
    return (
      parsed.protocol === "http:" &&
      (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1" || parsed.hostname === "::1")
    );
  } catch {
    return false;
  }
}

export function statusLabel(value: string): string {
  const labels: Record<string, string> = {
    customer_account_available: "Account ready",
    needs_customer_auth: "Sign-in needed",
    handoff_required: "Support required"
  };
  if (labels[value]) {
    return labels[value];
  }
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatPrice(value: unknown, currency: string): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "";
  }
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

export function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/`/g, "&#096;");
}

export const publicInternalUuidPattern =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

export function attr(element: HTMLElement, name: string): string {
  return element.getAttribute(name)?.trim() ?? "";
}
